// Authentication utilities - Using httpOnly cookies for security

// API URL configuration - use relative paths in production
export const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8001'
  : '';
export const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8001/api'
  : '/api';

// Helper function to get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// R10: Get CSRF token from cookie for state-changing requests
export const getCSRFToken = () => {
  return getCookie('csrf_token');
};

// Legacy localStorage keys - DEPRECATED, kept only for cleanup
const TOKEN_KEY = 'edubridge_token';
const USER_KEY = 'edubridge_user';

export const setAuth = (token, user) => {
  // R1 SECURITY: Do NOT store JWT tokens in localStorage (XSS risk)
  // The backend sets httpOnly cookies for auth_token automatically
  // We only store non-sensitive user display data for UI purposes

  if (user) {
    // Store only non-sensitive user display data
    // Note: This is also available via user_data cookie from backend
    localStorage.setItem('user', JSON.stringify({
      id: user.id,
      name: user.name,
      role: user.role,
      tenant_id: user.tenant_id
      // Intentionally omitting email for privacy
    }));

    // Notify BrandingContext that user data changed (for tenant branding)
    window.dispatchEvent(new CustomEvent('auth-user-updated', {
      detail: { tenant_id: user.tenant_id }
    }));
  }

  // Clear any legacy token storage (migration cleanup)
  localStorage.removeItem('token');
  localStorage.removeItem(TOKEN_KEY);
};

export const getToken = () => {
  // For httpOnly cookies, we can't access the token directly from JavaScript
  // The browser will automatically include it in requests
  // Return null since we shouldn't access httpOnly cookies from JS
  return null;
};

export const getUser = () => {
  // First try to get user from localStorage (JWT auth)
  const userFromStorage = localStorage.getItem('user');
  if (userFromStorage) {
    try {
      return JSON.parse(userFromStorage);
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
  }
  
  // Try to get user data from non-httpOnly cookie (fallback)
  const userDataCookie = getCookie('user_data');
  if (userDataCookie) {
    try {
      // Remove quotes if present (browser may add them)
      let cleanCookie = userDataCookie;
      if (cleanCookie.startsWith('"') && cleanCookie.endsWith('"')) {
        cleanCookie = cleanCookie.slice(1, -1);
      }
      
      // Backend sends base64-encoded JSON
      const decodedJson = atob(cleanCookie);
      return JSON.parse(decodedJson);
    } catch (e) {
      console.warn('Failed to decode/parse user data cookie:', e);
      
      // If we can't decode the cookie, it's probably from an old session
      // Clear the invalid cookie by setting it to expire
      document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // Fallback: try parsing as direct JSON (for backward compatibility)
      try {
        return JSON.parse(decodeURIComponent(userDataCookie));
      } catch (fallbackError) {
        console.warn('All cookie parsing attempts failed, clearing invalid cookie');
        // Cookie is corrupted, return null to force re-authentication
        return null;
      }
    }
  }
  
  // Fall back to localStorage for backward compatibility
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const clearAuth = () => {
  // Clear localStorage data first
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  console.log('🧹 Cleared localStorage auth data');

  // Notify BrandingContext that user logged out (reset to platform defaults)
  window.dispatchEvent(new CustomEvent('auth-user-updated', {
    detail: { tenant_id: null }
  }));

  // Call logout endpoint to clear httpOnly cookies
  fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include', // Include cookies in request
    headers: {
      'Content-Type': 'application/json',
    },
  }).catch(error => {
    console.warn('Error during logout:', error);
  });

  // Clear all authentication cookies manually (in case backend fails)
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

  // Clear localStorage for backward compatibility
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Force clear all auth data (for troubleshooting)
export const forceCleanAuth = () => {
  console.log('🧹 Force clearing all authentication data...');
  
  // Clear all possible authentication cookies
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'user_data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  
  // Clear localStorage
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  
  // Clear all localStorage keys that might be auth-related
  Object.keys(localStorage).forEach(key => {
    if (key.includes('auth') || key.includes('token') || key.includes('user')) {
      localStorage.removeItem(key);
    }
  });
  
  console.log('✅ All authentication data cleared');
};

export const isAuthenticated = () => {
  // Check if user data exists (indicates active session)
  const user = getUser();
  return !!user;
};

export const getAuthHeaders = () => {
  // R1 SECURITY: Do NOT use localStorage for tokens
  // Auth is handled via httpOnly cookies (credentials: 'include')
  const headers = {
    'Content-Type': 'application/json'
  };

  // R10: Include CSRF token for state-changing requests
  const csrfToken = getCSRFToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
};

// New function for making authenticated requests with cookies
// R1 SECURITY: Uses httpOnly cookies for auth, NOT localStorage
export const authenticatedFetch = (url, options = {}) => {
  // R10: Get CSRF token for state-changing requests
  const csrfToken = getCSRFToken();

  return fetch(url, {
    ...options,
    credentials: 'include', // httpOnly auth_token cookie sent automatically
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...options.headers,
    },
  });
};

// Get the correct dashboard path based on user role
export const getDashboardPath = (user = null) => {
  const currentUser = user || getUser();
  
  if (!currentUser) {
    return '/login';
  }
  
  switch (currentUser.role) {
    case 'superadmin':
      return '/superadmin-dashboard';
    case 'partner':
    case 'tenant_admin':
      return '/partner-dashboard';
    case 'student':
    default:
      return '/dashboard';
  }
};

// Navigate to the correct dashboard based on user role
export const navigateToDashboard = (navigate, user = null) => {
  const dashboardPath = getDashboardPath(user);
  navigate(dashboardPath);
};

// Role-based page access control
export const PAGE_PERMISSIONS = {
  // Student-accessible pages
  '/dashboard': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/profile': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/universities': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/programs': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/cart': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/applications': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/saved-programs': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/swipe-programs': ['student', 'partner', 'tenant_admin', 'superadmin'],
  
  // AI Services (accessible to all authenticated users)
  '/ai/services': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/ai/recommendations': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/ai/document-analysis': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/ai/application-assistant': ['student', 'partner', 'tenant_admin', 'superadmin'],
  '/ai/essay-review': ['student', 'partner', 'tenant_admin', 'superadmin'],
  
  // Partner/Admin pages
  '/partner-priorities': ['partner', 'tenant_admin', 'superadmin'],
  '/program-sequence': ['partner', 'tenant_admin', 'superadmin'],
  '/catalog-settings': ['partner', 'tenant_admin', 'superadmin'],
  '/partner-dashboard': ['partner', 'tenant_admin', 'superadmin'],
  '/analytics': ['tenant_admin', 'superadmin'],
  '/settings/tenant': ['tenant_admin', 'superadmin'],
  
  // Admin/Superadmin only pages
  '/admin/ai-config': ['tenant_admin', 'superadmin'],
  '/admin/data-ingestion': ['tenant_admin', 'superadmin'],
  
  // Superadmin only pages
  '/superadmin-dashboard': ['superadmin'],
  '/admin/tenants': ['superadmin'],
  '/admin/audit-logs': ['superadmin'],
  '/admin/universities': ['superadmin']
};

// Check if user has access to a specific page
export const hasPageAccess = (path, user = null) => {
  const currentUser = user || getUser();
  
  if (!currentUser) {
    return false;
  }
  
  // Remove query parameters and fragments for matching
  const cleanPath = path.split('?')[0].split('#')[0];
  
  // Check exact path match first
  if (PAGE_PERMISSIONS[cleanPath]) {
    return PAGE_PERMISSIONS[cleanPath].includes(currentUser.role);
  }
  
  // Check pattern matches for dynamic routes
  for (const [permissionPath, roles] of Object.entries(PAGE_PERMISSIONS)) {
    if (permissionPath.includes(':') || permissionPath.includes('*')) {
      // Handle dynamic routes like /programs/:id
      const pattern = permissionPath.replace(':id', '[^/]+').replace('*', '.*');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(cleanPath)) {
        return roles.includes(currentUser.role);
      }
    }
  }
  
  // Default: allow access to unspecified pages (like login, register, etc.)
  return true;
};

// Get user role display name
export const getRoleDisplayName = (role) => {
  const roleNames = {
    'student': 'Student',
    'partner': 'Partner',
    'tenant_admin': 'Tenant Admin',
    'superadmin': 'Super Admin'
  };
  return roleNames[role] || role;
};

// Check if user has admin privileges
export const isAdmin = (user = null) => {
  const currentUser = user || getUser();
  return currentUser && ['tenant_admin', 'superadmin'].includes(currentUser.role);
};

// Check if user is superadmin
export const isSuperAdmin = (user = null) => {
  const currentUser = user || getUser();
  return currentUser && currentUser.role === 'superadmin';
};

// Get accessible navigation items based on user role
export const getNavigationItems = (user = null) => {
  const currentUser = user || getUser();
  
  if (!currentUser) {
    return [];
  }
  
  const allNavItems = [
    // Student items
    { path: '/dashboard', label: '🏠 Dashboard', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    { path: '/profile', label: '👤 Profile', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    { path: '/universities', label: '🎓 Universities', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    { path: '/programs', label: '📚 Programs', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    { path: '/applications', label: '📝 Applications', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    { path: '/ama/explore', label: '🧭 Ask Guru', roles: ['student', 'partner', 'tenant_admin', 'superadmin'] },
    
    // Partner/Admin items
    { path: '/partner-dashboard', label: '📊 Partner Dashboard', roles: ['partner', 'tenant_admin', 'superadmin'] },
    { path: '/partner-priorities', label: '🏆 Priority Universities', roles: ['partner', 'tenant_admin', 'superadmin'] },
    { path: '/program-sequence', label: '📋 Program Sequence', roles: ['partner', 'tenant_admin', 'superadmin'] },
    { path: '/catalog-settings', label: '🌍 Catalog Settings', roles: ['partner', 'tenant_admin', 'superadmin'] },
    { path: '/analytics', label: '📈 Analytics', roles: ['tenant_admin', 'superadmin'] },
    
    // Admin items
    { path: '/admin/ai-config', label: '⚙️ AI Configuration', roles: ['tenant_admin', 'superadmin'] },
    { path: '/admin/data-ingestion', label: '📥 Data Ingestion', roles: ['tenant_admin', 'superadmin'] },
    
    // Superadmin items
    { path: '/superadmin-dashboard', label: '👑 Super Admin', roles: ['superadmin'] },
    { path: '/admin/tenants', label: '🏢 Manage Tenants', roles: ['superadmin'] },
    { path: '/admin/audit-logs', label: '📋 Audit Logs', roles: ['superadmin'] },
    { path: '/admin/universities', label: '🎓 Manage Universities', roles: ['superadmin'] }
  ];
  
  return allNavItems.filter(item => item.roles.includes(currentUser.role));
};

/**
 * Get a working logo URL for a university
 * Most logos are now stored in GCS (gs://guruskool-assets/university-logos/)
 * Legacy Clearbit URLs are transformed to Google Favicon as fallback
 * @param {string} logoUrl - Original logo URL from database (GCS or legacy Clearbit)
 * @param {string} universityName - University name for fallback avatar
 * @param {number} size - Desired size in pixels (default: 64)
 * @returns {string} Working logo URL
 */
export const getUniversityLogoUrl = (logoUrl, universityName = 'University', size = 64) => {
  // If no logo URL, return avatar fallback
  if (!logoUrl) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(universityName)}&size=${size}&background=6366f1&color=fff`;
  }

  // GCS URLs pass through directly (most universities)
  if (logoUrl.includes('storage.googleapis.com')) {
    return logoUrl;
  }

  // Transform legacy Clearbit URLs to Google Favicon (Clearbit is deprecated/403)
  if (logoUrl.includes('logo.clearbit.com')) {
    // Extract domain from Clearbit URL: https://logo.clearbit.com/utoronto.ca -> utoronto.ca
    const domain = logoUrl.replace('https://logo.clearbit.com/', '').split('/')[0];
    if (domain) {
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    }
  }

  // Return original URL for other sources
  return logoUrl;
};

/**
 * Get fallback avatar URL for when logo fails to load
 * @param {string} universityName - University name
 * @param {number} size - Desired size in pixels
 * @returns {string} Avatar URL
 */
export const getUniversityFallbackUrl = (universityName = 'University', size = 64) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(universityName)}&size=${size}&background=6366f1&color=fff`;
};

// WhatsApp configuration cache
let whatsappConfigCache = null;
let whatsappConfigPromise = null;

// Fetch tenant WhatsApp configuration (cached)
export const getTenantWhatsAppConfig = async () => {
  // Return cached config if available
  if (whatsappConfigCache) {
    return whatsappConfigCache;
  }

  // Don't fetch if user is not authenticated
  const user = getUser();
  if (!user) {
    return { enabled: false, number: null, message: '' };
  }

  // Return existing promise if fetch is in progress
  if (whatsappConfigPromise) {
    return whatsappConfigPromise;
  }

  // Fetch config from API
  whatsappConfigPromise = (async () => {
    try {
      const axios = (await import('axios')).default;
      // v1.4.41: Use public endpoint that doesn't require admin role
      // CRITICAL: withCredentials required for OAuth users (cookie-based auth)
      const response = await axios.get(`${API_URL}/tenant/public-settings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const settings = response.data?.settings || {};

      if (settings.whatsapp_enabled && settings.whatsapp_number) {
        whatsappConfigCache = {
          enabled: true,
          number: settings.whatsapp_number.replace(/[^0-9]/g, ''),
          message: settings.whatsapp_message || '',
        };
      } else {
        // No WhatsApp config - return null
        whatsappConfigCache = { enabled: false, number: null, message: '' };
      }

      return whatsappConfigCache;
    } catch (error) {
      // Silently fail - WhatsApp is optional
      whatsappConfigCache = { enabled: false, number: null, message: '' };
      return whatsappConfigCache;
    } finally {
      whatsappConfigPromise = null;
    }
  })();

  return whatsappConfigPromise;
};

// Clear WhatsApp config cache (call on logout)
export const clearWhatsAppConfigCache = () => {
  whatsappConfigCache = null;
  whatsappConfigPromise = null;
};

// Build WhatsApp URL with tenant number and custom message
export const buildWhatsAppUrl = (tenantNumber, customMessage) => {
  if (!tenantNumber) return null;
  const baseUrl = `https://wa.me/${tenantNumber}`;
  return customMessage ? `${baseUrl}?text=${encodeURIComponent(customMessage)}` : baseUrl;
};

// Tenant settings cache (general feature flags)
let tenantSettingsCache = null;
let tenantSettingsPromise = null;

// Fetch all tenant public settings (cached)
export const getTenantSettings = async () => {
  // Return cached settings if available
  if (tenantSettingsCache) {
    return tenantSettingsCache;
  }

  // Don't fetch if user is not authenticated
  const user = getUser();
  if (!user) {
    return {};
  }

  // Return existing promise if fetch is in progress
  if (tenantSettingsPromise) {
    return tenantSettingsPromise;
  }

  // Fetch settings from API
  tenantSettingsPromise = (async () => {
    try {
      const axios = (await import('axios')).default;
      const response = await axios.get(`${API_URL}/tenant/public-settings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      tenantSettingsCache = response.data?.settings || {};
      return tenantSettingsCache;
    } catch (error) {
      // Silently fail - settings are optional
      tenantSettingsCache = {};
      return tenantSettingsCache;
    } finally {
      tenantSettingsPromise = null;
    }
  })();

  return tenantSettingsPromise;
};

// Clear tenant settings cache (call on logout)
export const clearTenantSettingsCache = () => {
  tenantSettingsCache = null;
  tenantSettingsPromise = null;
};

// Currency symbol mapping by currency code
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  AED: 'AED ',
  SGD: 'S$',
  MYR: 'RM ',
  NOK: 'NOK ',
  INR: '₹',
};

// D1 fix: derive currency from country when tuition_currency is null
const COUNTRY_CURRENCY = {
  'United Kingdom': 'GBP',
  'UK': 'GBP',
  'Ireland': 'EUR',
  'Germany': 'EUR',
  'Netherlands': 'EUR',
  'France': 'EUR',
  'Spain': 'EUR',
  'Italy': 'EUR',
  'Sweden': 'SEK',
  'Norway': 'NOK',
  'Latvia': 'EUR',
  'Lithuania': 'EUR',
  'Czech Republic': 'EUR',
  'Slovenia': 'EUR',
  'Canada': 'CAD',
  'Australia': 'AUD',
  'New Zealand': 'NZD',
  'Singapore': 'SGD',
  'Malaysia': 'MYR',
  'United Arab Emirates': 'AED',
  'UAE': 'AED',
  'India': 'INR',
};

// Get currency symbol for a currency code
export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return '$';
  return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] || currencyCode + ' ';
};

// Format tuition amount - handles $0 and missing values gracefully
// Now supports currency parameter for local currency display
// D1 fix: country option used to derive currency when currency is null
export const formatTuition = (amount, options = {}) => {
  const { showPerYear = true, fallbackText = 'Contact for pricing', currency, country } = options;
  const resolvedCurrency = currency || (country ? COUNTRY_CURRENCY[country] : null) || 'USD';

  // Handle null, undefined, 0, or very low values (likely missing data)
  if (amount === null || amount === undefined || amount === 0 || amount < 100) {
    return fallbackText;
  }

  // Get the appropriate currency symbol
  const symbol = getCurrencySymbol(resolvedCurrency);

  // Format the amount
  const formatted = `${symbol}${amount.toLocaleString()}`;
  return showPerYear ? `${formatted}/year` : formatted;
};
